import { Download } from "lucide-react";
import { useCallback } from "react";
import { cn } from "@/lib/utils";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { useGeneratedFileViewer, decodeBase64ToBytes } from "./useGeneratedFileViewer";

export interface FileOutputChipFile {
  id: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  dataB64: string;
}

interface FileOutputChipProps {
  file: FileOutputChipFile;
  className?: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * FileOutputChip — displays a chatbot-generated file (e.g. a PDF) attached to
 * the assistant message. Clicking the chip body opens a preview dialog;
 * clicking the trailing download icon downloads the file directly.
 *
 * Backed by the `generated_files` field on the SSE complete event (WHA-3118).
 * Preview behavior added in WHA-3127.
 */
export function FileOutputChip({ file, className }: FileOutputChipProps) {
  const { filename, mime, sizeBytes, dataB64 } = file;
  const viewer = useGeneratedFileViewer();

  const handleDownload = useCallback(() => {
    if (!dataB64) return;
    const bytes = decodeBase64ToBytes(dataB64);
    const blob = new Blob([bytes], { type: mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [filename, mime, dataB64]);

  const openPreview = useCallback(() => {
    viewer.openViewer(file);
  }, [viewer, file]);

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "inline-flex max-w-full items-stretch overflow-hidden rounded-md border border-neutral-200",
                "bg-white text-xs text-neutral-800 transition-colors",
                "focus-within:ring-2 focus-within:ring-neutral-400",
                className
              )}
            >
              <button
                type="button"
                onClick={openPreview}
                className={cn(
                  "inline-flex min-w-0 items-center gap-2 px-2 py-1.5",
                  "hover:bg-neutral-50 active:bg-neutral-100",
                  "focus:outline-none"
                )}
              >
                <FileTypeIcon filename={filename} contentType={mime} className="h-7 w-7 shrink-0" />
                <span className="min-w-0 truncate font-medium">{filename}</span>
              </button>
              <button
                type="button"
                onClick={handleDownload}
                aria-label={`Download ${filename}`}
                className={cn(
                  "inline-flex shrink-0 items-center justify-center border-l border-neutral-200 px-2",
                  "text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 active:bg-neutral-100",
                  "focus:outline-none"
                )}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-sm font-medium">{filename}</p>
            <p className="text-xs text-neutral-400">Click to preview · {formatSize(sizeBytes)}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DocumentViewerDialog
        document={viewer.document}
        blobUrl={viewer.blobUrl}
        blobData={viewer.blobData}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        onDownloadOverride={handleDownload}
      />
    </>
  );
}

interface FileOutputChipGroupProps {
  files: FileOutputChipFile[];
  className?: string;
}

export function FileOutputChipGroup({ files, className }: FileOutputChipGroupProps) {
  if (!files.length) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {files.map((file) => (
        <FileOutputChip key={file.id} file={file} />
      ))}
    </div>
  );
}

FileOutputChip.displayName = "FileOutputChip";
FileOutputChipGroup.displayName = "FileOutputChipGroup";
